// src/utils/sessionManager.js
import apiRoutes from "../api/apiRoutes";
import {
  getAccessToken,
  setAccessToken,
  getTokenRemainingMs,
  logout,
} from "./auth";

import {
  readSessionClock,
  adoptSessionClock,
  tickSessionClock,
  getSharedRemainingMs,
} from "./sessionClock";

const STATE_KEY = "flpa_session_state";

const CLOCK_OWNER_KEY = "flpa_session_clock_owner";

const OPEN_TABS_KEY = "flpa_open_tabs";
const LAST_TAB_CLOSED_KEY = "flpa_last_tab_closed";
const TAB_RELOAD_KEY = "flpa_tab_reload";

const WARNING_BEFORE_EXP_MS = 1 * 60 * 1000;
const SAMPLE_MS = 1000;

const RENEW_RETRY_MS = 5000;
let lastRenewAttemptPerf = -Infinity;

const STATES = {
  NORMAL: "NORMAL",
  WARNING: "WARNING",
  RENEWING: "RENEWING",
  EXPIRED: "EXPIRED",
};

const TAB_ID =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${performance.now()}-${Math.random()}`;

let started = false;
let sampleTimer = null;
let onInactivityWarning = null;
let keepAliveInProgress = false;
let isClockOwner = false;

let lastLoggedSecond = null;
let lastMouseX = 0;
let lastMouseY = 0;

const MOUSE_THRESHOLD = 50;

const ACTIVE_REQUESTS_KEY = "flpa_active_requests";

function hasActiveLongProcess() {
  return Number(localStorage.getItem(ACTIVE_REQUESTS_KEY) || "0") > 0;
}

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readOpenTabs() {
  const tabs = readJson(OPEN_TABS_KEY);
  return Array.isArray(tabs) ? tabs : [];
}

function registerThisTab() {
  const tabs = readOpenTabs().filter((id) => id !== TAB_ID);
  tabs.push(TAB_ID);

  localStorage.setItem(
    OPEN_TABS_KEY,
    JSON.stringify(tabs)
  );
}

function unregisterThisTab() {
  const tabs = readOpenTabs().filter((id) => id !== TAB_ID);

  if (tabs.length > 0) {
    localStorage.setItem(
      OPEN_TABS_KEY,
      JSON.stringify(tabs)
    );
  } else {
    localStorage.removeItem(OPEN_TABS_KEY);
  }

  return tabs.length;
}

function validatePreviousClose() {
  const lastTabClosed =
    localStorage.getItem(LAST_TAB_CLOSED_KEY) === "1";

  if (!lastTabClosed) {
    sessionStorage.removeItem(TAB_RELOAD_KEY);
    return true;
  }

  const isReload =
    sessionStorage.getItem(TAB_RELOAD_KEY) === "1";

  localStorage.removeItem(LAST_TAB_CLOSED_KEY);
  sessionStorage.removeItem(TAB_RELOAD_KEY);

  if (isReload) {
    console.log(
      "[sessionManager] Recarga detectada. Se conserva la sesión."
    );
    return true;
  }

  console.log(
    "[sessionManager] La sesión anterior terminó al cerrar la última pestaña."
  );

  logout();
  return false;
}

function acquireClockOwnership() {
  const current = readJson(CLOCK_OWNER_KEY);

  // Ya hay otra pestaña dueña.
  if (current?.tabId && current.tabId !== TAB_ID) {
    const openTabs = readOpenTabs();

    if (openTabs.includes(current.tabId)) {
      return false;
    }

    // El owner registrado ya no figura como pestaña activa.
    localStorage.removeItem(CLOCK_OWNER_KEY);
  }

  const clock = readSessionClock();

  if (!clock || Number(clock.remainingMs) <= 0) {
    return false;
  }

  // Intentamos apropiarnos del reloj.
  localStorage.setItem(
    CLOCK_OWNER_KEY,
    JSON.stringify({
      tabId: TAB_ID,
    })
  );

  // Confirmamos que seguimos siendo nosotros.
  const actual = readJson(CLOCK_OWNER_KEY);

  if (actual?.tabId !== TAB_ID) {
    return false;
  }

  adoptSessionClock(Number(clock.remainingMs));
  isClockOwner = true;

  console.log(
    "[sessionManager] Esta pestaña tomó el reloj. tabId:",
    TAB_ID
  );

  return true;
}

function refreshClockOwnership() {
  if (!isClockOwner) return false;

  const current = readJson(CLOCK_OWNER_KEY);

  if (current?.tabId !== TAB_ID) {
    isClockOwner = false;
    return false;
  }

  return true;
}

function releaseClockOwnership() {
  const current = readJson(CLOCK_OWNER_KEY);

  if (current?.tabId === TAB_ID) {
    localStorage.removeItem(CLOCK_OWNER_KEY);
  }

  isClockOwner = false;
}

function onPageHide() {
  // sessionStorage sobrevive a F5, pero desaparece
  // cuando realmente se cierra la pestaña.
  sessionStorage.setItem(TAB_RELOAD_KEY, "1");

  releaseClockOwnership();

  const remainingTabs = unregisterThisTab();

  if (remainingTabs === 0) {
    localStorage.setItem(LAST_TAB_CLOSED_KEY, "1");
  }
}

function getSessionState() {
  return readJson(STATE_KEY) || { state: STATES.NORMAL };
}

function writeSessionState(state, extra = {}) {
  const payload = {
    state,
    tabId: TAB_ID,
    ...extra,
  };

  localStorage.setItem(STATE_KEY, JSON.stringify(payload));
  applySessionState(payload);
}

function notifyWarning(visible) {
  if (typeof onInactivityWarning === "function") {
    onInactivityWarning(visible === true);
  }
}

function remainingSec() {
  return Math.floor(getTokenRemainingMs() / 1000);
}

function logTime(label) {
  const sec = remainingSec();
  const current = getSessionState();

  console.log(
    `[sessionManager] ${label} | restante=${sec}s | estado=${current.state} | tabId=${TAB_ID}`
  );
}

function logEachSecond() {
  const sec = remainingSec();

  if (sec !== lastLoggedSecond) {
    lastLoggedSecond = sec;
    logTime("control");
  }
}

function expireSession(reason = "token_expired") {
  console.warn("[sessionManager] EXPIRED:", reason);

  writeSessionState(STATES.EXPIRED, { reason });
  notifyWarning(false);
  stopSessionWatch();
  logout();
}

function applySessionState(payload) {
  const state = payload?.state;

  if (state === STATES.WARNING) {
    notifyWarning(true);
    return;
  }

  if (state === STATES.NORMAL || state === STATES.RENEWING) {
    notifyWarning(false);
    return;
  }

  if (state === STATES.EXPIRED) {
    notifyWarning(false);

    if (getAccessToken()) {
      stopSessionWatch();
      logout();
    }
  }
}

async function callKeepAlive() {
  const tokenUsed = getAccessToken();
  if (!tokenUsed) return false;

  if (keepAliveInProgress) return false;
  keepAliveInProgress = true;

  console.log("[sessionManager] ==> keepAlive iniciado. tabId:", TAB_ID);

  try {
    const res = await fetch(apiRoutes.auth.keepAlive, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${tokenUsed}`,
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      let bodyText = "";

      try {
        bodyText = await res.text();
      } catch {}

      if (res.status === 401 && bodyText.includes("absolute_session_expired")) {
        expireSession("absolute_session_expired");
        return false;
      }

      if (getTokenRemainingMs() <= 0) {
        expireSession("keep_alive_failed_expired");
        return false;
      }

      console.warn("[sessionManager] keepAlive falló, token todavía vigente.");
      return false;
    }

    const data = await res.json();
    const newToken = data?.accessToken || data?.access_token;

    if (!newToken) {
      console.warn("[sessionManager] keepAlive OK pero sin token nuevo.");
      return false;
    }

    const saved = setAccessToken(newToken);

    if (!saved) {
      console.error("[sessionManager] Token renovado inválido.");
      return false;
    }

    console.log("[sessionManager] Token renovado correctamente.");
    logTime("post-renew");

    return true;
  } catch (e) {
    console.error("[sessionManager] keepAlive error:", e);
    return false;
  } finally {
    keepAliveInProgress = false;
  }
}

async function renewFromThisTab() {
  if (!isClockOwner) {
    console.log(
      "[sessionManager] Renovación delegada a pestaña dueña."
    );
    return;
  }

  const nowPerf = performance.now();

  if (nowPerf - lastRenewAttemptPerf < RENEW_RETRY_MS) {
    return;
  }

  lastRenewAttemptPerf = nowPerf;

  const ok = await callKeepAlive();

  if (ok) {
    writeSessionState(STATES.NORMAL);
    console.log("[sessionManager] Estado global -> NORMAL");
    return;
  }

  if (getTokenRemainingMs() <= 0) {
    expireSession("renew_failed_expired");
    return;
  }

  writeSessionState(STATES.WARNING);
  console.warn("[sessionManager] Estado global vuelve a WARNING.");
}

function tick() {
  if (!getAccessToken()) {
    notifyWarning(false);
    return;
  }

  if (!isClockOwner) {
    acquireClockOwnership();
  }

  let remainingMs;

  if (isClockOwner && !refreshClockOwnership()) {
    isClockOwner = false;
  }

  if (isClockOwner) {
    remainingMs = tickSessionClock(TAB_ID);
  } else {
    remainingMs = getSharedRemainingMs();
  }

  if (remainingMs == null) {
    return;
  }

  logEachSecond();
  const current = getSessionState();

  if (remainingMs <= 0) {
    expireSession("token_expired");
    return;
  }

  if (current.state === STATES.EXPIRED) {
    return;
  }

  if (current.state === STATES.RENEWING) {
    if (isClockOwner) {
      renewFromThisTab();
    }
    return;
  }

  if (current.state === STATES.NORMAL && remainingMs <= WARNING_BEFORE_EXP_MS) {
    if (hasActiveLongProcess()) {
      console.log("[sessionManager] Proceso activo: renovación silenciosa.");
      writeSessionState(STATES.RENEWING);

      if (isClockOwner) {
        renewFromThisTab();
      }

      return;
    }

    console.log("[sessionManager] Estado global -> WARNING");
    writeSessionState(STATES.WARNING);
  }
}

function onActivity(e) {
  if (typeof document !== "undefined" && document.hidden) return;

  if (e?.type === "mousemove") {
    const dist =
      Math.abs(e.screenX - lastMouseX) + Math.abs(e.screenY - lastMouseY);

    if (dist < MOUSE_THRESHOLD) return;

    lastMouseX = e.screenX;
    lastMouseY = e.screenY;
  }

  const current = getSessionState();

  if (current.state !== STATES.WARNING) return;

  console.log("[sessionManager] Actividad detectada en WARNING.");
  writeSessionState(STATES.RENEWING);
  console.log("[sessionManager] Estado global -> RENEWING");

  if (isClockOwner) {
    renewFromThisTab();
  }
}

function onStorage(e) {
  if (e.key === STATE_KEY) {
    const state = readJson(STATE_KEY);
    console.log("[sessionManager] storage STATE:", state?.state);
    applySessionState(state);
    return;
  }

  if (e.key === "access_token") {
    if (e.newValue === null) {
      notifyWarning(false);
      stopSessionWatch();
      return;
    }

    console.log("[sessionManager] storage token actualizado.");
    logTime("token-update");
    applySessionState(getSessionState());
  }
}

export function startSessionWatch(whereTag = "App.js", options = {}) {
  if (typeof options.onInactivityWarning === "function") {
    onInactivityWarning = options.onInactivityWarning;
  }

  if (started) {
    console.warn("[sessionManager] start ignorado, ya iniciado:", whereTag);
    return;
  }

  if (!validatePreviousClose()) {
    return;
  }

  if (!getAccessToken()) return;

  registerThisTab();

  started = true;

  console.info("[sessionManager] iniciado en:", whereTag, "tabId:", TAB_ID);
  logTime("inicio");

  window.addEventListener("storage", onStorage);
  window.addEventListener("pagehide", onPageHide);

  ["click", "keydown", "mousemove", "scroll", "touchstart", "app:activity"].forEach(
    (ev) => window.addEventListener(ev, onActivity, { passive: true })
  );

  const current = getSessionState();

  if (!current?.state || current.state === STATES.EXPIRED) {
    writeSessionState(STATES.NORMAL);
  } else {
    applySessionState(current);
  }

  sampleTimer = setInterval(tick, SAMPLE_MS);
  tick();
}

export function stopSessionWatch() {
  if (!started) return;

  started = false;

  window.removeEventListener("storage", onStorage);
  window.removeEventListener("pagehide", onPageHide);

  ["click", "keydown", "mousemove", "scroll", "touchstart", "app:activity"].forEach(
    (ev) => window.removeEventListener(ev, onActivity)
  );

  if (sampleTimer) {
    clearInterval(sampleTimer);
    sampleTimer = null;
  }

  unregisterThisTab();

  releaseClockOwnership();

  notifyWarning(false);
  onInactivityWarning = null;
}